import { useEffect, useState } from "react";
import { Loader2, Plus, Trash2, Network } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { supabase } from "@/integrations/supabase/client";

interface Cluster {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  target_persona: string | null;
  pillar_article_id: string | null;
  linked_service: string | null;
}

interface ClusterStats { id: string; article_count: number; pillar_title: string | null }

const slugify = (s: string) => s.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

const ClustersTab = () => {
  const [rows, setRows] = useState<Cluster[]>([]);
  const [stats, setStats] = useState<Record<string, ClusterStats>>({});
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState("");
  const [orphans, setOrphans] = useState(0);

  const load = async () => {
    setLoading(true);
    const [{ data: clusters }, { data: articles }] = await Promise.all([
      supabase.from("content_clusters").select("*").order("name"),
      supabase.from("blog_articles").select("id, title, cluster_id"),
    ]);
    setRows((clusters ?? []) as Cluster[]);
    const map: Record<string, ClusterStats> = {};
    let orphanCount = 0;
    for (const a of articles ?? []) {
      if (!a.cluster_id) { orphanCount++; continue; }
      if (!map[a.cluster_id]) map[a.cluster_id] = { id: a.cluster_id, article_count: 0, pillar_title: null };
      map[a.cluster_id].article_count++;
    }
    for (const c of clusters ?? []) {
      if (c.pillar_article_id) {
        const p = (articles ?? []).find((a) => a.id === c.pillar_article_id);
        if (p && map[c.id]) map[c.id].pillar_title = p.title;
      }
    }
    setStats(map);
    setOrphans(orphanCount);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!newName.trim()) return;
    const slug = slugify(newName);
    const { error } = await supabase.from("content_clusters").insert({ name: newName.trim(), slug } as never);
    if (error) { toast.error("Couldn't create", { description: error.message }); return; }
    setNewName(""); toast.success("Cluster created"); load();
  };

  const remove = async (id: string) => {
    if (!confirm("Delete this cluster? Articles will become orphans.")) return;
    await supabase.from("content_clusters").delete().eq("id", id);
    load();
  };

  const update = async (id: string, patch: Partial<Cluster>) => {
    setRows((rs) => rs.map((r) => r.id === id ? { ...r, ...patch } : r));
    await supabase.from("content_clusters").update(patch as never).eq("id", id);
  };

  return (
    <div className="space-y-4">
      <div className="rounded-xl border p-4 flex flex-wrap items-end gap-3"
        style={{ background: "hsl(0 0% 7%)", borderColor: "hsl(0 0% 14%)" }}>
        <div className="flex-1 min-w-[240px]">
          <label className="text-xs font-medium mb-1.5 block" style={{ color: "hsl(0 0% 55%)" }}>New cluster</label>
          <Input value={newName} onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && add()}
            placeholder="e.g. Cold Calling Operations"
            className="border" style={{ background: "hsl(0 0% 5%)", borderColor: "hsl(0 0% 18%)", color: "hsl(0 0% 90%)" }} />
        </div>
        <Button onClick={add} disabled={!newName.trim()} style={{ background: "hsl(207 68% 50%)", color: "white" }}>
          <Plus size={14} className="mr-1" /> Create
        </Button>
        {orphans > 0 && (
          <div className="basis-full text-xs rounded-lg border px-3 py-2"
            style={{ background: "hsl(38 92% 55% / 0.08)", borderColor: "hsl(38 92% 55% / 0.3)", color: "hsl(38 92% 70%)" }}>
            {orphans} article{orphans === 1 ? "" : "s"} not assigned to any cluster.
          </div>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-32" style={{ color: "hsl(0 0% 50%)" }}>
          <Loader2 size={16} className="animate-spin mr-2" /> Loading…
        </div>
      ) : rows.length === 0 ? (
        <div className="rounded-xl border p-12 text-center"
          style={{ background: "hsl(0 0% 6%)", borderColor: "hsl(0 0% 14%)" }}>
          <Network size={28} className="mx-auto mb-3" style={{ color: "hsl(0 0% 35%)" }} />
          <p className="text-base font-medium" style={{ color: "hsl(0 0% 80%)" }}>No clusters yet</p>
          <p className="text-sm mt-1" style={{ color: "hsl(0 0% 55%)" }}>Group articles by topic to build topical authority.</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {rows.map((c) => {
            const s = stats[c.id];
            return (
              <div key={c.id} className="rounded-xl border p-4 space-y-3"
                style={{ background: "hsl(0 0% 6%)", borderColor: "hsl(0 0% 14%)" }}>
                <div className="flex items-start justify-between">
                  <div className="min-w-0">
                    <Input value={c.name} onChange={(e) => update(c.id, { name: e.target.value })}
                      className="border-0 px-0 h-auto bg-transparent text-base font-semibold focus-visible:ring-0"
                      style={{ color: "hsl(0 0% 92%)" }} />
                    <div className="text-[11px]" style={{ color: "hsl(0 0% 45%)" }}>/{c.slug}</div>
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => remove(c.id)}
                    className="h-7 w-7 p-0" style={{ color: "hsl(0 75% 60%)" }}><Trash2 size={12} /></Button>
                </div>
                <Textarea value={c.description ?? ""} onChange={(e) => update(c.id, { description: e.target.value })}
                  rows={2} placeholder="Cluster description / scope"
                  className="border resize-none text-sm" style={{ background: "hsl(0 0% 5%)", borderColor: "hsl(0 0% 18%)", color: "hsl(0 0% 85%)" }} />
                <Input value={c.target_persona ?? ""} onChange={(e) => update(c.id, { target_persona: e.target.value })}
                  placeholder="Target persona"
                  className="border text-sm" style={{ background: "hsl(0 0% 5%)", borderColor: "hsl(0 0% 18%)", color: "hsl(0 0% 85%)" }} />
                <Input value={c.linked_service ?? ""} onChange={(e) => update(c.id, { linked_service: e.target.value })}
                  placeholder="Linked service (e.g. cold-calling)"
                  className="border text-sm" style={{ background: "hsl(0 0% 5%)", borderColor: "hsl(0 0% 18%)", color: "hsl(0 0% 85%)" }} />
                <div className="flex items-center justify-between text-xs pt-2 border-t" style={{ borderColor: "hsl(0 0% 12%)" }}>
                  <span style={{ color: "hsl(0 0% 55%)" }}>{s?.article_count ?? 0} article{(s?.article_count ?? 0) === 1 ? "" : "s"}</span>
                  <span style={{ color: "hsl(0 0% 55%)" }}>
                    {s?.pillar_title ? <>Pillar: <span style={{ color: "hsl(207 68% 65%)" }}>{s.pillar_title}</span></> : "No pillar set"}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ClustersTab;
